import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from '@tanstack/react-router';
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  IconButton,
  InputAdornment,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Description as FileIcon,
  Folder as FolderIcon,
  InsertLink as LinkIcon,
  Search as SearchIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import {
  useAppendToFavoriteMutation,
  useDownloadFileMutation,
  useGetFolderItemsQuery,
  useLazyGetFolderItemsQuery,
  useLazyGetItemPathQuery,
  useLazySearchFilesQuery,
  type FileStorageItem,
} from './api/fileStorageApi';
import { normalizeUrl } from './utils/url';
import { downloadFromResponse, sortFileItems, type FileSortKey } from './utils/fileStorageHelpers';
import { FileStorageSearchResults } from './components/file-storage/FileStorageSearchResults';
import {
  ACCESS_DENIED_MESSAGE,
  extractErrorMessage,
  isAccessDeniedError,
} from './utils/fileStorageErrors';
import {
  buildFileStoragePath,
  isRootFolderParentId,
  normalizePathname,
  parseFileStoragePath,
} from './utils/fileStoragePath';
import { getPathFromBreadcrumb } from './utils/fileStorageBreadcrumb';
import { FileStorageAccessDeniedPanel } from './components/file-storage/FileStorageAccessDeniedPanel';
import { FileStorageItemsTable } from './components/file-storage/FileStorageItemsTable';
import { FileStoragePathHeader } from './components/common/FileStoragePathHeader';

const fileIcon = (item: FileStorageItem) => {
  if (item.isFolder) return <FolderIcon fontSize="small" color="warning" />;
  if (item.isUrl) return <LinkIcon fontSize="small" color="info" />;
  return <FileIcon fontSize="small" color="action" />;
};

const STORAGE_VIEW_KEYS = {
  all: 'all',
  favorites: 'favorites',
} as const;
type StorageViewKey = (typeof STORAGE_VIEW_KEYS)[keyof typeof STORAGE_VIEW_KEYS];

const STORAGE_VIEWS = {
  [STORAGE_VIEW_KEYS.all]: { label: 'All', storageType: 'All files' },
  [STORAGE_VIEW_KEYS.favorites]: { label: 'Favorites', storageType: 'Favorite' },
} as const;

export const FileStoragePage = ({ nodeKey, title }: { nodeKey?: string; title?: string }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { basePath, folderSegments, selectedFile } = useMemo(
    () => parseFileStoragePath(pathname),
    [pathname]
  );
  const [view, setView] = useState<StorageViewKey>(STORAGE_VIEW_KEYS.all);
  const [currentNodeKey, setCurrentNodeKey] = useState<string | undefined>(nodeKey);
  const [pathStack, setPathStack] = useState<Array<{ id: string; name: string }>>([]);
  const [selectedFileId, setSelectedFileId] = useState<string | null>(null);
  const [isPathSyncing, setIsPathSyncing] = useState(() => folderSegments.length > 0);
  const [pathAccessError, setPathAccessError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [sortKey, setSortKey] = useState<FileSortKey>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const filesTableRef = useRef<HTMLDivElement | null>(null);
  const isSearchDirty = searchTerm.trim() !== activeSearch;
  const storageType = STORAGE_VIEWS[view].storageType;
  const isFavoritesView = view === STORAGE_VIEW_KEYS.favorites;

  const {
    data: folderData,
    isLoading,
    isFetching,
    error,
  } = useGetFolderItemsQuery({ nodeKey: currentNodeKey, storageType }, { skip: isPathSyncing });
  const [triggerSearch, { data: searchResults, isFetching: isSearching }] =
    useLazySearchFilesQuery();
  const [triggerGetFolderItems] = useLazyGetFolderItemsQuery();
  const [triggerGetItemPath] = useLazyGetItemPathQuery();
  const [appendToFavorite, { isLoading: isFavoriteUpdating }] = useAppendToFavoriteMutation();
  const [downloadFile, { isLoading: isDownloading }] = useDownloadFileMutation();

  const folderItems = useMemo(() => {
    return folderData?.fileItems ?? [];
  }, [folderData?.fileItems]);
  const displayItems = useMemo(() => {
    if (!isFavoritesView) return folderItems;
    return folderItems.filter((item) => !item.isFolder);
  }, [isFavoritesView, folderItems]);
  const favoriteSearchResults = useMemo(() => {
    if (!isFavoritesView) return [];
    const q = activeSearch.trim().toLowerCase();
    if (!q) return [];
    return displayItems.filter((item) => item.name.toLowerCase().includes(q));
  }, [activeSearch, displayItems, isFavoritesView]);
  const sortedItems = useMemo(() => {
    return sortFileItems(displayItems, sortKey, sortDirection);
  }, [displayItems, sortKey, sortDirection]);
  const folderPath = useMemo(
    () =>
      buildFileStoragePath(
        basePath,
        pathStack.map((segment) => segment.name)
      ),
    [basePath, pathStack]
  );
  const fileItems = useMemo(() => sortedItems.filter((item) => !item.isFolder), [sortedItems]);
  const navigateIfPathChanged = useCallback(
    (nextPath: string, replace = false) => {
      if (normalizePathname(pathname) !== nextPath) {
        navigate({ to: nextPath, replace });
      }
    },
    [navigate, pathname]
  );

  const handleSort = (key: FileSortKey) => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDirection('asc');
  };

  const handleOpenItem = async (item: FileStorageItem) => {
    if (item.isFolder) {
      if (isFavoritesView) {
        setView(STORAGE_VIEW_KEYS.all);
      }
      const nextPathStack = [...pathStack, { id: item.id, name: item.name }];
      setCurrentNodeKey(item.id);
      setPathStack(nextPathStack);
      setSelectedFileId(null);
      setActiveSearch('');
      const nextPath = buildFileStoragePath(
        basePath,
        nextPathStack.map((segment) => segment.name),
        null
      );
      navigateIfPathChanged(nextPath);
      return;
    }
    if (item.isUrl && item.link) {
      window.open(normalizeUrl(item.link), '_blank', 'noopener,noreferrer');
      return;
    }
    if (!item.id) return;
    setDownloadError(null);
    try {
      const response = await downloadFile({ key: item.id }).unwrap();
      if (!downloadFromResponse(response, item.name)) {
        setDownloadError(
          'Could not decode the file. The download data may be invalid or corrupted.'
        );
      }
    } catch {
      setDownloadError('Failed to download the file.');
    }
  };

  const handleOpenParentFolder = () => {
    if (!folderData?.haveParentId) return;
    const nextPathStack = pathStack.slice(0, -1);
    setPathStack(nextPathStack);
    const fallbackParentNodeKey = folderData.parentId || undefined;
    setCurrentNodeKey(nextPathStack[nextPathStack.length - 1]?.id ?? fallbackParentNodeKey);
    setSelectedFileId(null);
    setActiveSearch('');
    const nextPath = buildFileStoragePath(
      basePath,
      nextPathStack.map((segment) => segment.name),
      null
    );
    navigateIfPathChanged(nextPath);
  };

  const handleOpenBreadcrumb = (segmentIndex: number) => {
    const nextPathStack = getPathFromBreadcrumb(pathStack, segmentIndex);
    if (!nextPathStack) return;
    setPathStack(nextPathStack);
    setCurrentNodeKey(nextPathStack[segmentIndex]?.id || nodeKey);
    setSelectedFileId(null);
    setActiveSearch('');

    const nextPath = buildFileStoragePath(
      basePath,
      nextPathStack.map((segment) => segment.name),
      null
    );
    navigateIfPathChanged(nextPath);
  };

  const handleSearch = useCallback(
    async (rawValue: string) => {
      const value = rawValue.trim();
      if (!value) {
        setActiveSearch('');
        return;
      }
      setActiveSearch(value);
      if (view === STORAGE_VIEW_KEYS.favorites) {
        return;
      }
      try {
        await triggerSearch({ query: value, storageType }).unwrap();
      } catch {
        // Keep local folder filtering active even when global API search fails.
      }
    },
    [storageType, triggerSearch, view]
  );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void handleSearch(searchTerm);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [handleSearch, searchTerm]);

  useEffect(() => {
    let isCancelled = false;
    if (isFavoritesView) {
      setIsPathSyncing(false);
      setPathAccessError(null);
      return;
    }
    const currentPathNames = pathStack.map((item) => item.name);
    const normalizedUrlSegments = folderSegments.map((segment) => segment.trim().toLowerCase());
    const normalizedCurrentPathNames = currentPathNames.map((name) => name.trim().toLowerCase());
    const isPathAlreadySynced =
      normalizedUrlSegments.length === normalizedCurrentPathNames.length &&
      normalizedUrlSegments.every(
        (segment, index) => segment === normalizedCurrentPathNames[index]
      );

    if (isPathAlreadySynced) {
      setIsPathSyncing(false);
      setPathAccessError(null);
      return;
    }

    const syncFromPath = async () => {
      try {
        setIsPathSyncing(true);
        let parentNodeKey = nodeKey;
        const resolvedStack: Array<{ id: string; name: string }> = [];
        let isPathFullyResolved = true;

        for (const segment of folderSegments) {
          const folderResponse = await triggerGetFolderItems(
            { nodeKey: parentNodeKey, storageType: 'All files' },
            true
          ).unwrap();

          const matchedFolder = folderResponse.fileItems.find(
            (item) => item.isFolder && item.name.toLowerCase() === segment.toLowerCase()
          );

          if (!matchedFolder?.id) {
            isPathFullyResolved = false;
            break;
          }

          resolvedStack.push({ id: matchedFolder.id, name: matchedFolder.name });
          parentNodeKey = matchedFolder.id;
        }

        if (isCancelled) return;

        if (!isPathFullyResolved) {
          setPathAccessError(ACCESS_DENIED_MESSAGE);
          setCurrentNodeKey((prev) => (prev === nodeKey ? prev : nodeKey));
          setPathStack((prev) => (prev.length === 0 ? prev : []));
          setSelectedFileId((prev) => (prev === null ? prev : null));
          setActiveSearch((prev) => (prev === '' ? prev : ''));
          return;
        }

        setCurrentNodeKey(parentNodeKey);
        setPathStack(resolvedStack);
        setSelectedFileId(null);
        setActiveSearch('');
        setPathAccessError(null);

        const canonicalPath = buildFileStoragePath(
          basePath,
          resolvedStack.map((item) => item.name),
          selectedFile?.id ? { id: selectedFile.id, name: selectedFile.slug } : null
        );
        navigateIfPathChanged(canonicalPath, true);
      } catch (syncError) {
        if (!isCancelled) {
          if (isAccessDeniedError(syncError)) {
            setPathAccessError(ACCESS_DENIED_MESSAGE);
          }
          setCurrentNodeKey((prev) => (prev === nodeKey ? prev : nodeKey));
          setPathStack((prev) => (prev.length === 0 ? prev : []));
        }
      } finally {
        if (!isCancelled) {
          setIsPathSyncing(false);
        }
      }
    };

    void syncFromPath();
    return () => {
      isCancelled = true;
    };
  }, [
    basePath,
    folderSegments,
    isFavoritesView,
    navigateIfPathChanged,
    nodeKey,
    pathStack,
    selectedFile?.id,
    selectedFile?.slug,
    triggerGetFolderItems,
  ]);

  useEffect(() => {
    if (!error || pathAccessError) return;
    if (!isAccessDeniedError(error)) return;
    setPathAccessError(ACCESS_DENIED_MESSAGE);
  }, [error, pathAccessError]);

  const handleReturnToRoot = () => {
    setPathAccessError(null);
    setPathStack([]);
    setCurrentNodeKey(nodeKey);
    setSelectedFileId(null);
    setActiveSearch('');
    const rootPath = buildFileStoragePath(basePath, []);
    navigateIfPathChanged(rootPath, true);
  };

  const handleScrollToFileInList = (item: FileStorageItem) => {
    if (!activeSearch || !isFavoritesView) return;
    if (!sortedItems.some((entry) => entry.id === item.id)) return;
    setSelectedFileId(item.id);
  };

  const handleShowInDirectory = async (item: FileStorageItem) => {
    if (!activeSearch || isFavoritesView) return;

    const parentFolderId = item.parentItemId;
    const isRootParent = isRootFolderParentId(parentFolderId);

    setSearchTerm('');
    setActiveSearch('');
    setPathAccessError(null);
    setIsPathSyncing(false);

    let resolvedStack: Array<{ id: string; name: string }> = [];
    try {
      if (!isRootParent && parentFolderId) {
        resolvedStack = await triggerGetItemPath({ itemId: parentFolderId }, true).unwrap();
      }
    } catch {
      // Fall back to opening the parent folder without a resolved breadcrumb path.
    }

    setPathStack(resolvedStack);
    setCurrentNodeKey(isRootParent ? nodeKey : (parentFolderId ?? nodeKey));
    setSelectedFileId(item.id);

    const nextPath = buildFileStoragePath(
      basePath,
      resolvedStack.map((segment) => segment.name),
      { id: item.id, name: item.name }
    );
    navigateIfPathChanged(nextPath);
  };

  const handleToggleFavorite = async (item: FileStorageItem) => {
    if (item.isFolder) return;
    await appendToFavorite({ fileId: item.id, status: item.isFavorite }).unwrap();
  };

  const searchCards = isFavoritesView ? favoriteSearchResults : (searchResults ?? []);
  const showProgress =
    isPathSyncing || isLoading || isFetching || isSearching || isFavoriteUpdating || isDownloading;

  const handleClearSearch = () => {
    setSearchTerm('');
    setActiveSearch('');
  };

  const navigateWithSelection = useCallback(
    (nextSelectedId: string | null) => {
      if (isFavoritesView) return;
      const selectedItem = nextSelectedId
        ? fileItems.find((item) => item.id === nextSelectedId)
        : null;
      const nextPath = buildFileStoragePath(
        basePath,
        pathStack.map((segment) => segment.name),
        selectedItem ? { id: selectedItem.id, name: selectedItem.name } : null
      );
      navigateIfPathChanged(nextPath, true);
    },
    [basePath, fileItems, isFavoritesView, navigateIfPathChanged, pathStack]
  );

  const clearSelectedFile = useCallback(() => {
    setSelectedFileId(null);
    if (!isFavoritesView) {
      navigateWithSelection(null);
    }
  }, [isFavoritesView, navigateWithSelection]);

  useEffect(() => {
    if (!selectedFileId) return;
    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (filesTableRef.current?.contains(target)) return;
      clearSelectedFile();
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [clearSelectedFile, selectedFileId]);

  useEffect(() => {
    if (!selectedFile?.id || isPathSyncing || !!pathAccessError) return;
    if (isFavoritesView) return;
    if (isLoading || isFetching || !folderData) return;
    const matchedFile = fileItems.find((item) => item.id === selectedFile.id);
    if (!matchedFile) {
      navigateIfPathChanged(folderPath, true);
      return;
    }

    setSelectedFileId((prev) => (prev === matchedFile.id ? prev : matchedFile.id));
    const canonicalFilePath = buildFileStoragePath(
      basePath,
      pathStack.map((segment) => segment.name),
      { id: matchedFile.id, name: matchedFile.name }
    );
    navigateIfPathChanged(canonicalFilePath, true);
  }, [
    basePath,
    fileItems,
    folderData,
    folderPath,
    isFetching,
    isLoading,
    isFavoritesView,
    isPathSyncing,
    pathAccessError,
    navigateIfPathChanged,
    pathStack,
    selectedFile?.id,
  ]);

  useEffect(() => {
    if (!isFavoritesView) return;
    if (isPathSyncing) return;
    if (folderSegments.length > 0 || selectedFile?.id) {
      const favoritesPath = buildFileStoragePath(basePath, []);
      navigateIfPathChanged(favoritesPath, true);
    }
  }, [
    basePath,
    folderSegments.length,
    isFavoritesView,
    isPathSyncing,
    navigateIfPathChanged,
    selectedFile?.id,
  ]);

  const shouldShowAccessDeniedPanel =
    !!pathAccessError && folderSegments.length > 0 && pathStack.length === 0;
  const shouldShowPathSyncLoader =
    isPathSyncing && folderSegments.length > 0 && !shouldShowAccessDeniedPanel;

  return (
    <Container maxWidth={false}>
      <Box sx={{ mt: 1 }}>
        <FileStoragePathHeader
          rootLabel={title || 'My files'}
          segments={pathStack}
          onRootClick={handleReturnToRoot}
          onSegmentClick={handleOpenBreadcrumb}
        />
        <Box
          sx={{
            mt: 1,
            mb: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
          }}
        >
          <ToggleButtonGroup
            exclusive
            size="small"
            value={view}
            onChange={(_, next: StorageViewKey | null) => {
              if (!next) return;
              setView(next);
              setIsPathSyncing(false);
              setCurrentNodeKey(undefined);
              setPathStack([]);
              setSelectedFileId(null);
              setSearchTerm('');
              setActiveSearch('');
              setPathAccessError(null);
              if (next === STORAGE_VIEW_KEYS.favorites) {
                const favoritesPath = buildFileStoragePath(basePath, []);
                navigateIfPathChanged(favoritesPath, true);
              }
            }}
          >
            {(
              Object.entries(STORAGE_VIEWS) as Array<
                [StorageViewKey, (typeof STORAGE_VIEWS)[StorageViewKey]]
              >
            ).map(([key, cfg]) => (
              <ToggleButton key={key} value={key}>
                {cfg.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        {downloadError && (
          <Alert severity="error" onClose={() => setDownloadError(null)} sx={{ mb: 1.5 }}>
            {downloadError}
          </Alert>
        )}

        {shouldShowAccessDeniedPanel ? (
          <FileStorageAccessDeniedPanel
            message={pathAccessError ?? ACCESS_DENIED_MESSAGE}
            onReturnToRoot={handleReturnToRoot}
          />
        ) : shouldShowPathSyncLoader ? (
          <Box
            sx={{
              mt: 1.5,
              p: 2,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            <TextField
              size="small"
              fullWidth
              placeholder="Search files"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void handleSearch(searchTerm);
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {isSearching && <CircularProgress size={16} sx={{ mr: 0.75 }} />}
                    {searchTerm && (
                      <IconButton size="small" onClick={handleClearSearch}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )}
                    <IconButton
                      size="small"
                      color={isSearchDirty ? 'primary' : 'default'}
                      onClick={() => void handleSearch(searchTerm)}
                    >
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <FileStorageSearchResults
              activeSearch={activeSearch}
              items={searchCards}
              actionLabel={isFavoritesView ? 'Scroll to file' : 'Show in directory'}
              onAction={(item) =>
                isFavoritesView ? handleScrollToFileInList(item) : void handleShowInDirectory(item)
              }
            />

            {error ? (
              <Alert severity="error" sx={{ mt: 1.5 }}>
                {extractErrorMessage(error, 'Failed to load files.')}
              </Alert>
            ) : (
              <Box ref={filesTableRef}>
                <FileStorageItemsTable
                  folderData={folderData}
                  sortedItems={sortedItems}
                  showProgress={showProgress}
                  sortKey={sortKey}
                  sortDirection={sortDirection}
                  itemIcon={fileIcon}
                  selectedFileId={selectedFileId}
                  onSort={handleSort}
                  onOpenParent={handleOpenParentFolder}
                  onOpenItem={handleOpenItem}
                  onToggleFavorite={handleToggleFavorite}
                />
              </Box>
            )}
          </>
        )}
      </Box>
    </Container>
  );
};
